import { formatarDia } from '@common/i18n/formatosData';
import { Badge, Button } from '@ntconsult/aluma-react';
import { useTranslation } from 'react-i18next';
import type { FiltrosUsuarios } from '../types/Usuario';

type Chave = keyof FiltrosUsuarios;
/** `valor`: o item do filtro de lista que este chip representa (um chip por valor). */
type Chip = { chave: Chave; texto: string; valor?: string };

/** Chips removíveis dos filtros aplicados e "Limpar tudo" (spec 35 R7). */
export function UsuariosChips({
  filtros: f,
  onRemover,
  onLimpar,
}: {
  filtros: FiltrosUsuarios;
  onRemover: (parcial: Partial<FiltrosUsuarios>) => void;
  onLimpar: () => void;
}) {
  const { t } = useTranslation('usuarios');

  const chips: Chip[] = [];
  const add = (chave: Chave, filtro: string, valor: string, item?: string) =>
    chips.push({ chave, texto: t('filtros.rotulo', { filtro, valor }), valor: item });
  const cada = (
    chave: Chave,
    filtro: string,
    valores: readonly string[],
    nome: (v: string) => string,
  ) => {
    for (const v of valores) add(chave, filtro, nome(v), v);
  };

  if (f.nome) add('nome', t('colunas.nome'), f.nome);
  if (f.email) add('email', t('colunas.email'), f.email);
  cada('perfil', t('colunas.perfis'), f.perfil, (v) => t(`filtros.perfis.${v}` as never));
  cada('entrou', t('filtros.entrou.rotulo'), f.entrou, (v) => t(`filtros.entrou.${v}` as never));
  if (f.ultimoLoginDe || f.ultimoLoginAte)
    add(
      'ultimoLoginDe',
      t('colunas.ultimoLogin'),
      [
        f.ultimoLoginDe && formatarDia(f.ultimoLoginDe),
        f.ultimoLoginAte && formatarDia(f.ultimoLoginAte),
      ]
        .filter(Boolean)
        .join(' – '),
    );
  cada('vinculo', t('colunas.cadastro'), f.vinculo, (v) => t(`vinculo.${v}` as never));

  if (chips.length === 0) return null;

  /** Cada chip zera o campo do filtro que o originou; o de lista tira só o seu valor. */
  const zerar = ({ chave, valor }: Chip): Partial<FiltrosUsuarios> => {
    const atual = f[chave];
    if (Array.isArray(atual)) return { [chave]: atual.filter((v) => v !== valor) };
    if (chave === 'ultimoLoginDe') return { ultimoLoginDe: '', ultimoLoginAte: '' };
    return { [chave]: '' };
  };

  return (
    <fieldset className="flex min-w-0 flex-wrap items-center gap-2">
      <legend className="sr-only">{t('filtros.aplicados')}</legend>
      {chips.map((c) => (
        <Badge
          key={`${c.chave}:${c.valor ?? ''}`}
          variant="outline"
          className="min-w-0 max-w-full"
          title={c.texto}
          onRemove={() => onRemover(zerar(c))}
          removeLabel={t('filtros.remover', { filtro: c.texto })}
        >
          <span className="min-w-0 truncate">{c.texto}</span>
        </Badge>
      ))}
      <Button variant="secondary" size="sm" onClick={onLimpar}>
        {t('filtros.limpar')}
      </Button>
    </fieldset>
  );
}
